import { WX_ID_MSM } from "../../../store/WeatherContentsState";
import MsmUpper from "./MsmUpper";
import MsmGround from "./MsmGround";
import { _orderBy, calcPressureFromAlt, ft2m, translation } from "../../../utils/FPEUtils";
import LegendMsm from "../legend/LegendMsm";
import TimeController from "../helper/TimeController";
import Mutex from "../../../utils/Mutex";
import LegendMsmSub from "../legend/LegendMsmSub";

class Msm {
  static NAME = WX_ID_MSM;
  static MSM_GROUND = `${Msm.NAME}_Ground`;
  static MSM_UPPER = `${Msm.NAME}_Upper`;
  static WIND_ARROW_NAME = `${Msm.NAME}_WIND_ARROW`;
  static WIND_STREAM_NAME = `${Msm.NAME}_WIND_STREAM`;
  static ISOTACH_CONTOUR_NAME = `${Msm.NAME}_ISOTACH_CONTOUR`;
  static ISOTACH_GRADATION_NAME = `${Msm.NAME}_ISOTACH_GRADATION`;
  static ISOTHERM_CONTOUR_NAME = `${Msm.NAME}_ISOTHERM_CONTOUR`;
  static ISOTHERM_GRADATION_NAME = `${Msm.NAME}_ISOTHERM_GRADATION`;
  static ISOTHERM_GRID_NAME = `${Msm.NAME}_ISOTHERM_VALUE`;
  static GEO_POTENTIAL_HEIGHT_NAME = `${Msm.NAME}_GEO_POTENTIAL_HEIGHT`;
  static ICING_PROB_NAME = `${Msm.NAME}_ICING_PROB_NAME`;
  static PRECIPITATION_1H_MESH_NAME = `${Msm.NAME}_PRECIPITATION_1H_MESH`;
  static PRECIPITATION_1H_CONTOUR_NAME = `${Msm.NAME}_PRECIPITATION_1H_CONTOUR`;
  static PRECIPITATION_1H_GRID_NAME = `${Msm.NAME}_PRECIPITATION_1H_GRID`;
  static RELATIVE_HUMIDITY_CONTOUR_NAME = `${Msm.NAME}_RELATIVE_HUMIDITY_CONTOUR`;
  static RELATIVE_HUMIDITY_GRADATION_NAME = `${Msm.NAME}_RELATIVE_HUMIDITY_GRADATION`;
  static MSM_UPPER_LEVEL = {
    "100": "FL530 / 100hPa",
    "150": "FL450 / 150hPa",
    "200": "FL390 / 200hPa",
    "250": "FL340 / 250hPa",
    "300": "FL300 / 300hPa",
    "400": "FL240 / 400hPa",
    "500": "FL180 / 500hPa",
    "600": "FL140 / 600hPa",
    "700": "FL100 / 700hPa",
    "800": "FL070 / 800hPa",
    "850": "FL050 / 850hPa",
    "900": "FL030 / 900hPa",
    "925": "FL025 / 925hPa",
    "950": "FL020 / 950hPa",
    "975": "FL010 / 975hPa",
    "1000":"FL005 / 1000hPa",
  };
  static MSM_GROUND_CONTENTS_KEYS = [
    Msm.WIND_ARROW_NAME,
    Msm.WIND_STREAM_NAME,
    Msm.ISOTACH_CONTOUR_NAME,
    Msm.ISOTACH_GRADATION_NAME,
    Msm.ISOTHERM_CONTOUR_NAME,
    Msm.ISOTHERM_GRADATION_NAME,
    Msm.ISOTHERM_GRID_NAME,
    Msm.ICING_PROB_NAME,
    Msm.PRECIPITATION_1H_MESH_NAME,
    Msm.PRECIPITATION_1H_CONTOUR_NAME,
    Msm.PRECIPITATION_1H_GRID_NAME,
  ];
  static MSM_UPPER_CONTENTS_KEYS = [
    Msm.WIND_ARROW_NAME,
    Msm.WIND_STREAM_NAME,
    Msm.ISOTACH_CONTOUR_NAME,
    Msm.ISOTACH_GRADATION_NAME,
    Msm.ISOTHERM_CONTOUR_NAME,
    Msm.ISOTHERM_GRADATION_NAME,
    Msm.ISOTHERM_GRID_NAME,
    Msm.ICING_PROB_NAME,
    Msm.GEO_POTENTIAL_HEIGHT_NAME,
    Msm.RELATIVE_HUMIDITY_CONTOUR_NAME,
    Msm.RELATIVE_HUMIDITY_GRADATION_NAME,
  ];
  static CONTENTS_VISIBLE_STATE_DEFAULT = {
    [Msm.WIND_ARROW_NAME]: false,
    [Msm.WIND_STREAM_NAME]: true,
    [Msm.ISOTACH_CONTOUR_NAME]: true,
    [Msm.ISOTACH_GRADATION_NAME]: false,
    [Msm.ISOTHERM_CONTOUR_NAME]: true,
    [Msm.ISOTHERM_GRADATION_NAME]: false,
    [Msm.ISOTHERM_GRID_NAME]: false,
    [Msm.GEO_POTENTIAL_HEIGHT_NAME]: true,
    [Msm.ICING_PROB_NAME]: true,
    [Msm.PRECIPITATION_1H_MESH_NAME]: true,
    [Msm.PRECIPITATION_1H_CONTOUR_NAME]: true,
    [Msm.PRECIPITATION_1H_GRID_NAME]: false,
    [Msm.RELATIVE_HUMIDITY_CONTOUR_NAME]: true,
    [Msm.RELATIVE_HUMIDITY_GRADATION_NAME]: false,
  };
  keyMap = {
    [Msm.WIND_ARROW_NAME]: {
      groundKey: MsmGround.WIND_ARROW_NAME,
      upperKey: MsmUpper.WIND_ARROW_NAME
    },
    [Msm.WIND_STREAM_NAME]: {
      groundKey: MsmGround.WIND_STREAM_NAME,
      upperKey: MsmUpper.WIND_STREAM_NAME
    },
    [Msm.ISOTACH_CONTOUR_NAME]: {
      groundKey: MsmGround.ISOTACH_CONTOUR_NAME,
      upperKey: MsmUpper.ISOTACH_CONTOUR_NAME
    },
    [Msm.ISOTACH_GRADATION_NAME]: {
      groundKey: MsmGround.ISOTACH_GRADATION_NAME,
      upperKey: MsmUpper.ISOTACH_GRADATION_NAME
    },
    [Msm.ISOTHERM_CONTOUR_NAME]: {
      groundKey: MsmGround.ISOTHERM_CONTOUR_NAME,
      upperKey: MsmUpper.ISOTHERM_CONTOUR_NAME
    },
    [Msm.ISOTHERM_GRADATION_NAME]: {
      groundKey: MsmGround.ISOTHERM_GRADATION_NAME,
      upperKey: MsmUpper.ISOTHERM_GRADATION_NAME
    },
    [Msm.ISOTHERM_GRID_NAME]: {
      groundKey: MsmGround.ISOTHERM_GRID_NAME,
      upperKey: MsmUpper.ISOTHERM_GRID_NAME
    },
    [Msm.GEO_POTENTIAL_HEIGHT_NAME]: {
      groundKey: undefined,
      upperKey: MsmUpper.GEO_POTENTIAL_HEIGHT_NAME
    },
    [Msm.ICING_PROB_NAME]: {
      groundKey: MsmGround.ICING_PROB_NAME,
      upperKey: MsmUpper.ICING_PROB_NAME
    },
    [Msm.PRECIPITATION_1H_MESH_NAME]: {
      groundKey: MsmGround.PRECIPITATION_1H_MESH_NAME,
      upperKey: undefined
    },
    [Msm.PRECIPITATION_1H_CONTOUR_NAME]: {
      groundKey: MsmGround.PRECIPITATION_1H_CONTOUR_NAME,
      upperKey: undefined
    },
    [Msm.PRECIPITATION_1H_GRID_NAME]: {
      groundKey: MsmGround.PRECIPITATION_1H_GRID_NAME,
      upperKey: undefined
    },
    [Msm.RELATIVE_HUMIDITY_CONTOUR_NAME]: {
      groundKey: undefined,
      upperKey: MsmUpper.RELATIVE_HUMIDITY_CONTOUR_NAME
    },
    [Msm.RELATIVE_HUMIDITY_GRADATION_NAME]: {
      groundKey: undefined,
      upperKey: MsmUpper.RELATIVE_HUMIDITY_GRADATION_NAME
    }
  };
  viewGround = true;
  altitude = 0;
  level = '0';
  contentsVisibleState = {...Msm.CONTENTS_VISIBLE_STATE_DEFAULT};
  // MSMタイムコントローラー
  msmTimeController;
  // タイムコントローラーのリスト
  timeList
  // 自動更新フラグ
  autoRefresh = true;

  constructor(isDark) {
    this.msmTimeController = new TimeController(WX_ID_MSM, {
      label: 'MSM',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
    this.msmUpper = new MsmUpper(isDark, this.msmTimeController);
    this.msmGround = new MsmGround(isDark, this.msmTimeController);
    this.msmUpper.setUpdateTimeFunc(this._updateTimeList.bind(this));
    this.msmGround.setUpdateTimeFunc(this._updateTimeList.bind(this));
    this.drawLayerMutex = new Mutex();
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    const cascade = (action) => {
      if (action.type === 'SET_CONTENTS_TIME') {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      } else {
        return this.dispatcher(action);
      }
    };
    this.msmUpper.setDispatcher(cascade);
    this.msmGround.setDispatcher(cascade);
  }

  setModelDispatcher(dispatcher) {
    this.modelDispatcher = dispatcher;
  }

  async drawLayer(visible) {
    this.drawLayerMutex.runExclusion(async () => {
      this.visible = visible;

      if (visible) {
        //visible = falseの切り替えから行う(TimeController絡みの最適化）
        if (this.viewGround) {
          await Promise.all([
            this.msmUpper.drawLayer(false),
            this.msmGround.drawLayer(true)
          ]);
        } else {
          await Promise.all([
            this.msmGround.drawLayer(false),
            this.msmUpper.drawLayer(true)
          ]);
        }
      } else {
        await Promise.all([
          this.msmGround.drawLayer(false),
          this.msmUpper.drawLayer(false)
        ]);
      }
    });
  }

  getLayer() {
    return [...this.msmUpper.getLayer(), ...this.msmGround.getLayer()];
  }

  getTimeInformation() {
    let info;
    if(this.viewGround) {
      info = this.msmGround.getTimeInformation();
    } else {
      info = this.msmUpper.getTimeInformation();
    }
    info.id = Msm.NAME;
    return info;
  }

  getLegend() {
    return {
      id: Msm.NAME,
      label: 'MSM',
      component: LegendMsm,
      subComponent: LegendMsmSub,
      contents: this
    };
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  getIndividualAltitude() {
    return {
      id: Msm.NAME,
      altitude: this.altitude,
      label: 'MSM',
      tooltip: this._altitudeTooltip
    };
  }

  getAltitudeLabel() {
    return this._altitudeTooltip(this.altitude);
  }

  applyIndividualAltitude(altitude) {
    if(this.altitude !== altitude) {
      if(altitude === 0) {
        this.viewGround = true;
      } else {
        this.viewGround = false;
        this.msmUpper.applyIndividualAltitude(altitude);
      }
      this.drawLayer(this.visible);
      this.altitude = altitude;
    }
  }

  applyElapsed(elapsed) {
    this.msmGround.applyElapsed(elapsed);
    this.msmUpper.applyElapsed(elapsed);
  }

  applyPlanETD(etd) {
    this.msmGround.applyPlanETD(etd);
    this.msmUpper.applyPlanETD(etd);
  }

  getTimeController() {
    return this.msmTimeController;
  }

  setAutoRefresh(autoFlg) {
    this.autoRefresh = autoFlg;
    this.msmGround.setAutoRefresh(autoFlg);
    this.msmUpper.setAutoRefresh(autoFlg);
  }

  _altitudeTooltip(altitude) {
    if(altitude === 0) return translation('Ground');
    const msm = [
      {fl: 530, mb: 100},
      {fl: 450, mb: 150},
      {fl: 390, mb: 200},
      {fl: 340, mb: 250},
      {fl: 300, mb: 300},
      {fl: 240, mb: 400},
      {fl: 180, mb: 500},
      {fl: 140, mb: 600},
      {fl: 100, mb: 700},
      {fl: 70, mb: 800},
      {fl: 50, mb: 850},
      {fl: 30, mb: 900},
      {fl: 25, mb: 925},
      {fl: 20, mb: 950},
      {fl: 10, mb: 975},
      {fl: 5, mb: 1000},
    ]

    const m = ft2m(altitude * 100);
    const mb = calcPressureFromAlt(m);
    const near = _orderBy(msm, [
      o => {
        return Math.abs(mb - o.mb);
      },
      o => {
        return o;
      }
    ])[0];
    const fl = ('000' + near.fl).slice(-3);
    return `FL${fl} / ${near.mb}hPa`;
  }

  getDataType() {
    return this.viewGround ? Msm.MSM_GROUND : Msm.MSM_UPPER;
  }

  getContentsKeys() {
    return this.viewGround ? Msm.MSM_GROUND_CONTENTS_KEYS : Msm.MSM_UPPER_CONTENTS_KEYS;
  }

  resetContentsVisibleState(csAllOff) {
    if (Object.keys(this.contentsVisibleState)
      .filter(key => this.getContentsKeys(this.getDataType()).includes(key))
      .filter(key => this.contentsVisibleState[key]).length === 0 && csAllOff) {
      this.contentsVisibleState = {...Msm.CONTENTS_VISIBLE_STATE_DEFAULT};
      this.msmGround.resetContentsVisibleState();
      this.msmUpper.resetContentsVisibleState();
      return true;
    }
    return false;
  }

  applyMapColor(mapColor, isDark) {
    this.msmGround.applyMapColor(mapColor, isDark);
    this.msmUpper.applyMapColor(mapColor, isDark);
  }

  async setVisible(visible, contents) {
    if (this.contentsVisibleState.hasOwnProperty(contents)) {
      this.contentsVisibleState[contents] = visible;

      const key = this.keyMap[contents];
      if (key?.groundKey !== undefined) {
        this.msmGround.setVisible(visible, key.groundKey);
      }
      if (key?.upperKey !== undefined) {
        this.msmUpper.setVisible(visible, key.upperKey);
      }
    }
    await this.modelDispatcher({type: 'SET_MODEL_CONTENTS_VISIBLE', payload: {targetName: contents, visible}});
  }

  _updateTimeList() {
    let timelist;
    if (this.viewGround) {
      timelist = this.msmGround?.dh?.retained?.timelist?.timelist;
    } else {
      timelist = this.msmUpper?.dh?.retained?.timelist?.timelist;
    }

    if (!timelist) {
      return;
    }

    // 最新のbasetimeのvalidtimeを取得
    const latestBasetime = _orderBy(timelist, ['basetime'], ['desc'])[0];
    if (!latestBasetime.validtime) {
      return;
    }
    this.timeList = Array.from(latestBasetime.validtime).sort();

    if (this.autoRefresh) {
      if (this.viewGround) {
        this.msmTimeController.setCurrentTime(this.msmGround.validTime);
      } else {
        this.msmTimeController.setCurrentTime(this.msmUpper.validTime);
      }
    }
    this.msmTimeController.adjustCurrentTime();
    this.msmTimeController.invalidate();
  }

  getLevels() {
    const labels = Object.keys(Msm.MSM_UPPER_LEVEL)
      .sort((a,b) => { return a - b;})
      .map(value => {
        return {
          value,
          label: Msm.MSM_UPPER_LEVEL[value]
        };
      });
    labels.push({value: "0", label: translation('Ground')});
    return labels;
  }
  async setLevel(level) {
    this.modelDispatcher({type: 'SET_MODEL_LEVEL', payload: level});

    if (this.level === level) return;
    this.level = level;
    await this.setViewGround(level === "0");
    if (level !== "0") {
      this.msmUpper.applyIndividualLevel(level, Msm.MSM_UPPER_LEVEL[level]);
    }
  }
  getLevel() {
    return this.level;
  }
  async setViewGround(viewGround) {
    if (this.viewGround === viewGround) return;
    this.viewGround = viewGround;
    await this.drawLayer(this.visible);
    this._updateTimeList();
  }

  isElementDisableAtLevel(element, level) {
    // 地上は高度による要素の制限はない
    if (this.viewGround) return false;

    const elementName = this.keyMap[element]?.upperKey;
    if (elementName === undefined) return true;

    return this.msmUpper.isElementDisableAtLevel(elementName, level)
  }
}

export default Msm;
